import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  VStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Select,
  HStack,
  Icon,
  useToast,
  Link,
} from '@chakra-ui/react';
import { FaPhone, FaWhatsapp, FaInstagram, FaMapMarkerAlt, FaEnvelope } from 'react-icons/fa';
import { contactInfo, practiceAreas } from '../lib/storage';
import type { FormData } from '../lib/types';

const Contact: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    area: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha todos os campos obrigatórios.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: 'E-mail inválido',
        description: 'Por favor, insira um endereço de email válido.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      setIsSubmitting(false);
      return;
    }

    const whatsappMessage = `
      *NOVO CONTATO - SITE MENDES ASSESSORIA*

      *Nome:* ${formData.name}
      *E-mail:* ${formData.email}
      *Telefone:* ${formData.phone}
      ${formData.area ? `*Área de Interesse:* ${formData.area}` : ''}

      *Mensagem:*
      ${formData.message}
    `.trim();

    const phoneNumber = contactInfo.whatsapp.replace(/\D/g, '');
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    window.open(whatsappURL, '_blank');

    toast({
      title: 'Redirecionando para WhatsApp!',
      description: 'Sua mensagem foi preparada. Complete o envio no WhatsApp.',
      status: 'success',
      duration: 5000,
      isClosable: true,
      position: 'top',
    });

    setFormData({
      name: '',
      email: '',
      phone: '',
      area: '',
      message: '',
    });

    setIsSubmitting(false);
  };

  return (
    <Box id="contact" ref={sectionRef} bg="alabasterGrey" py={{ base: 16, md: 24 }} position="relative">
      <Container maxW="1400px">
        <VStack spacing={12} align="stretch">
          <VStack
            spacing={4}
            textAlign="center"
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
            transition="all 0.8s ease-out"
          >
            <Heading as="h2" fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }} color="yaleBlue" fontWeight={700}>
              Entre em Contato
            </Heading>
            <Box w="80px" h="4px" bg="stormyTeal" borderRadius="2px" />
            <Text fontSize={{ base: 'md', md: 'lg' }} color="graphite" maxW="800px">
              Estamos prontos para auxiliá-lo. Preencha o formulário abaixo ou entre em contato pelos nossos canais.
            </Text>
          </VStack>

          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 8, lg: 12 }}
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
            transition="all 0.8s ease-out 0.2s"
          >
            <Box bg="white" p={{ base: 6, md: 8 }} borderRadius="8px" boxShadow="0 4px 12px rgba(0,0,0,0.1)">
              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  <FormControl isRequired>
                    <FormLabel color="graphite" fontWeight={600}>Nome Completo</FormLabel>
                    <Input name="name" value={formData.name} onChange={handleInputChange} placeholder="Seu nome completo" size="lg" focusBorderColor="stormyTeal" />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color="graphite" fontWeight={600}>E-mail</FormLabel>
                    <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="seu@email.com" size="lg" focusBorderColor="stormyTeal" />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color="graphite" fontWeight={600}>Telefone / WhatsApp</FormLabel>
                    <Input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="(00) 00000-0000" size="lg" focusBorderColor="stormyTeal" />
                  </FormControl>

                  <FormControl>
                    <FormLabel color="graphite" fontWeight={600}>Área de Interesse</FormLabel>
                    <Select name="area" value={formData.area} onChange={handleInputChange} placeholder="Selecione uma área" size="lg" focusBorderColor="stormyTeal">
                      {practiceAreas.map((area) => (
                        <option key={area.id} value={area.title}>{area.title}</option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color="graphite" fontWeight={600}>Mensagem</FormLabel>
                    <Textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Descreva brevemente seu caso ou dúvida" rows={6} size="lg" focusBorderColor="stormyTeal" resize="vertical" />
                  </FormControl>

                  <Button type="submit" variant="primary" size="lg" w="full" isLoading={isSubmitting} loadingText="Enviando...">
                    Enviar Mensagem
                  </Button>
                </VStack>
              </form>
            </Box>

            <VStack spacing={6} align="stretch">
              <Box bg="white" p={6} borderRadius="8px" boxShadow="0 4px 12px rgba(0,0,0,0.1)">
                <HStack spacing={4} align="flex-start">
                  <Icon as={FaPhone} boxSize={6} color="stormyTeal" mt={1} />
                  <VStack align="flex-start" spacing={2}>
                    <Text fontWeight={700} color="yaleBlue" fontSize="lg">Central de Atendimento</Text>
                    <Link href={`tel:${contactInfo.phone.replace(/\D/g, '')}`} fontSize="xl" fontWeight={600} color="graphite" _hover={{ color: 'stormyTeal' }}>
                      {contactInfo.phone}
                    </Link>
                  </VStack>
                </HStack>
              </Box>

              <Box bg="white" p={6} borderRadius="8px" boxShadow="0 4px 12px rgba(0,0,0,0.1)">
                <HStack spacing={4} align="flex-start">
                  <Icon as={FaWhatsapp} boxSize={6} color="stormyTeal" mt={1} />
                  <VStack align="flex-start" spacing={2}>
                    <Text fontWeight={700} color="yaleBlue" fontSize="lg">Atendimento por WhatsApp</Text>
                    <Link href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`} isExternal fontSize="xl" fontWeight={600} color="graphite" _hover={{ color: 'stormyTeal' }}>
                      {contactInfo.whatsapp}
                    </Link>
                  </VStack>
                </HStack>
              </Box>

              <Box bg="white" p={6} borderRadius="8px" boxShadow="0 4px 12px rgba(0,0,0,0.1)">
                <HStack spacing={4} align="flex-start">
                  <Icon as={FaInstagram} boxSize={6} color="stormyTeal" mt={1} />
                  <VStack align="flex-start" spacing={2}>
                    <Text fontWeight={700} color="yaleBlue" fontSize="lg">Nosso Instagram</Text>
                    <Link href={`https://instagram.com/${contactInfo.instagram}`} isExternal fontSize="xl" fontWeight={600} color="graphite" _hover={{ color: 'stormyTeal' }}>
                      @{contactInfo.instagram}
                    </Link>
                  </VStack>
                </HStack>
              </Box>

              <Box bg="white" p={6} borderRadius="8px" boxShadow="0 4px 12px rgba(0,0,0,0.1)">
                <HStack spacing={4} align="flex-start">
                  <Icon as={FaEnvelope} boxSize={6} color="stormyTeal" mt={1} />
                  <VStack align="flex-start" spacing={2}>
                    <Text fontWeight={700} color="yaleBlue" fontSize="lg">E-mail</Text>
                    <Link href={`mailto:${contactInfo.email}`} fontSize="lg" fontWeight={600} color="graphite" maxW="240px" _hover={{ color: 'stormyTeal' }}>
                      {contactInfo.email}
                    </Link>
                  </VStack>
                </HStack>
              </Box>

              <Box bg="yaleBlue" p={6} borderRadius="8px" boxShadow="0 4px 12px rgba(0,0,0,0.1)">
                <HStack spacing={4} align="flex-start">
                  <Icon as={FaMapMarkerAlt} boxSize={6} color="white" mt={1} />
                  <VStack align="flex-start" spacing={2}>
                    <Text fontWeight={700} color="white" fontSize="lg">Sede Matriz</Text>
                    <Text color="whiteAlpha.900" lineHeight="1.6">
                      {contactInfo.address}
                      <br />
                      {contactInfo.cityState}
                      <br />
                      {contactInfo.cep}
                      <br />
                      Tel: {contactInfo.phone}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </VStack>
          </SimpleGrid>

          <Box
            bg="white"
            borderRadius="8px"
            overflow="hidden"
            boxShadow="0 4px 12px rgba(0,0,0,0.1)"
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
            transition="all 0.8s ease-out 0.35s"
          >
            <Box p={{ base: 6, md: 8 }}>
              <Heading as="h3" fontSize={{ base: '2xl', md: '3xl' }} color="yaleBlue" mb={2}>
                Endereço da matriz
              </Heading>
              <Text color="graphite">
                {contactInfo.address}, {contactInfo.cityState} - {contactInfo.cep}
              </Text>
            </Box>
            <Box
              as="iframe"
              title="Mapa da sede matriz da Mendes Assessoria Jurídica"
              src={contactInfo.mapEmbedUrl}
              width="100%"
              height={{ base: '320px', md: '420px' }}
              border="0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Contact;


